/**
 * Shared helpers for "step ready to open" in AI workflow builder.
 * Dim/disable and add-path lock are based only on auth (not field mapping).
 * Built-in Workload tools that don't have hasauth are excluded from the disabled feature.
 */

/**
 * True if step requires an auth (OAuth app).
 * Only nodes with hasauth === true are considered; built-in tools/functions without auth are excluded.
 */
export function stepRequiresAuth(node) {
  if (!node?.data) return false;
  return node.data.hasauth === true;
}

/** True if step has a credential or auth name set. */
export function hasAuth(node) {
  if (!node?.data) return false;
  const d = node.data;
  return !!(d.credential || d.credentialId || (d.authname && String(d.authname).trim()));
}

function unwrapFieldValue(value) {
  let v = value;
  while (
    v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.prototype.hasOwnProperty.call(v, 'value')
  ) {
    v = v.value;
  }
  return v;
}

/** True if a field value is effectively empty (for required checks). */
export function isFieldValueEmpty(value) {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value)) {
    if ('value' in value || 'label' in value) {
      return value.value == null && (value.label == null || value.label === '');
    }
  }
  const unwrapped = unwrapFieldValue(value);
  if (unwrapped === undefined || unwrapped === null || unwrapped === '') return true;
  return false;
}

/**
 * True if all required fields (including body schema.required keys) have values.
 * Used to gate Test / Continue in ReadOnly panels.
 */
export function areRequiredFieldsFilled(fields) {
  if (!Array.isArray(fields) || fields.length === 0) return true;
  for (const field of fields) {
    if (!field || field.hidden === true || field?.schema?.hidden === true) continue;

    if (
      (field.name === 'body' || field.in === 'formData') &&
      field.schema?.type === 'object' &&
      field.schema?.properties
    ) {
      const bodyRequired = Array.isArray(field.schema.required) ? field.schema.required : [];
      const bodyValue =
        field.value && typeof field.value === 'object' && !Array.isArray(field.value)
          ? field.value
          : {};
      for (const key of bodyRequired) {
        const prop = field.schema.properties[key];
        if (prop?.hidden === true) continue;
        if (isFieldValueEmpty(bodyValue[key])) return false;
      }
      continue;
    }

    if (field.required === true || field.schema?.required === true) {
      if (isFieldValueEmpty(field.value)) return false;
    }
  }
  return true;
}

/** Labels for missing required fields (for helper copy). */
export function getMissingRequiredFieldLabels(fields) {
  const missing = [];
  if (!Array.isArray(fields)) return missing;
  for (const field of fields) {
    if (!field || field.hidden === true || field?.schema?.hidden === true) continue;
    if (
      (field.name === 'body' || field.in === 'formData') &&
      field.schema?.type === 'object' &&
      field.schema?.properties
    ) {
      const bodyRequired = Array.isArray(field.schema.required) ? field.schema.required : [];
      const bodyValue =
        field.value && typeof field.value === 'object' && !Array.isArray(field.value)
          ? field.value
          : {};
      for (const key of bodyRequired) {
        const prop = field.schema.properties[key];
        if (prop?.hidden === true) continue;
        if (isFieldValueEmpty(bodyValue[key])) {
          missing.push(prop?.title || key);
        }
      }
      continue;
    }
    if (field.required === true || field.schema?.required === true) {
      if (isFieldValueEmpty(field.value)) {
        missing.push(field.title || field.name || 'Field');
      }
    }
  }
  return missing;
}

export function isTestDataEmpty(testData) {
  if (testData == null) return true;
  if (typeof testData === 'string' && !String(testData).trim()) return true;
  if (typeof testData === 'object' && !Array.isArray(testData) && Object.keys(testData).length === 0) {
    return true;
  }
  return false;
}

/**
 * True if step has app, event, auth (when needed), and required fields — ready to test.
 */
export function canTestStep(node) {
  if (!node?.data) return false;
  const d = node.data;
  const hasApp = !!(d.app || d.type);
  const hasEvent = !!(d.action && String(d.action).trim());
  if (!hasApp || !hasEvent) return false;
  if (stepRequiresAuth(node) && !hasAuth(node)) return false;
  return areRequiredFieldsFilled(d.fields || []);
}

function isTestErrorPayload(testData) {
  if (!testData || typeof testData !== 'object') return false;
  if (Object.prototype.hasOwnProperty.call(testData, 'error')) return true;
  if (typeof testData.status_code === 'number' && testData.status_code >= 400) return true;
  return false;
}

/** Completion flags for Setup → Configure → Test wizard (and activation gating). */
export function getWizardStepState(node) {
  const d = node?.data || {};
  const setupDone =
    !!(d.app || d.type) &&
    !!(d.action && String(d.action).trim()) &&
    (!stepRequiresAuth(node) || hasAuth(node));
  const configureDone = canTestStep(node);
  const testDone = !isTestDataEmpty(d.testData) && !isTestErrorPayload(d.testData);
  return { setupDone, configureDone, testDone };
}

/** Initial wizard tab based on what's still incomplete. */
export function getDefaultWizardStep(node) {
  const { setupDone, configureDone } = getWizardStepState(node);
  if (!setupDone) return 'setup';
  if (!configureDone) return 'configure';
  return 'test';
}

/**
 * Canvas node badge color from Setup → Configure → Test completeness.
 * @returns {'ok'|'warning'|'error'}
 */
export function getCanvasBadgeStatus(node) {
  if (!node || (node.type !== 'trigger' && node.type !== 'action')) return 'ok';
  const { setupDone, configureDone, testDone } = getWizardStepState(node);
  if (!setupDone) return 'error';
  if (!configureDone || !testDone) return 'warning';
  return 'ok';
}

/** True if all required and dynamic fields have values. */
export function isStepReady(node) {
  const fields = node?.data?.fields || [];
  for (const f of fields) {
    if (f.required !== true && !(f.schema && f.schema.dynamic)) continue;
    const v = f.value;
    if (v == null) return false;
    if (typeof v === 'object' && (v.value == null && v.label == null)) return false;
  }
  return true;
}

/**
 * Progressive status for ReadOnly step panels.
 * Returns { key, label, tone } where tone is 'ready' | 'needs'.
 */
export function getStepConfigStatus(node) {
  if (!node?.data) {
    return { key: 'needs_app', label: 'Choose an app to get started', tone: 'needs' };
  }
  const d = node.data;
  const hasApp = !!(d.app || d.type);
  const hasEvent = !!(d.action && String(d.action).trim());

  if (!hasApp) {
    return { key: 'needs_app', label: 'Choose an app', tone: 'needs' };
  }
  if (!hasEvent) {
    return {
      key: 'needs_event',
      label: node.type === 'trigger' ? 'Choose a trigger event' : 'Choose an action event',
      tone: 'needs',
    };
  }
  if (stepRequiresAuth(node) && !hasAuth(node)) {
    return { key: 'needs_auth', label: 'Connect an account', tone: 'needs' };
  }
  if (!areRequiredFieldsFilled(d.fields || [])) {
    const missing = getMissingRequiredFieldLabels(d.fields || []);
    if (missing.length === 1) {
      return { key: 'needs_fields', label: `Set required field: ${missing[0]}`, tone: 'needs' };
    }
    if (missing.length > 1) {
      return {
        key: 'needs_fields',
        label: `Finish ${missing.length} required fields`,
        tone: 'needs',
      };
    }
    return { key: 'needs_fields', label: 'Finish required fields', tone: 'needs' };
  }
  if (isTestDataEmpty(d.testData)) {
    return {
      key: 'needs_test',
      label: node.type === 'trigger' ? 'Test to get sample data' : 'Test this step',
      tone: 'needs',
    };
  }
  if (
    typeof d.testData === 'object' &&
    d.testData &&
    (Object.prototype.hasOwnProperty.call(d.testData, 'error') ||
      (typeof d.testData.status_code === 'number' && d.testData.status_code >= 400))
  ) {
    return { key: 'test_failed', label: 'Test failed — retest to continue', tone: 'needs' };
  }
  return { key: 'ready', label: 'Ready', tone: 'ready' };
}

/** True if Setup, Configure, and Test are all complete for this step. */
export function isWizardStepFullyComplete(node) {
  if (!node || (node.type !== 'trigger' && node.type !== 'action')) return true;
  const { setupDone, configureDone, testDone } = getWizardStepState(node);
  return setupDone && configureDone && testDone;
}

/**
 * True if every trigger/action in the workflow has Setup → Configure → Test complete.
 * Used to gate workflow activation (ON switch).
 */
export function canActivateWorkflow(elements) {
  if (!Array.isArray(elements) || elements.length === 0) return false;
  const nodes = elements.filter((el) => el.id && !el.source && !el.target && (el.type === 'trigger' || el.type === 'action'));
  if (nodes.length === 0) return false;
  return nodes.every((node) => isWizardStepFullyComplete(node));
}

/** First incomplete step label for activation tooltip / toast. */
export function getActivationBlockerMessage(elements) {
  if (!Array.isArray(elements)) return 'Add a step to continue';
  const nodes = elements.filter((el) => el.id && !el.source && !el.target && (el.type === 'trigger' || el.type === 'action'));
  if (nodes.length === 0) return 'Add a trigger to continue';
  for (const node of nodes) {
    const { setupDone, configureDone, testDone } = getWizardStepState(node);
    const name = node.data?.name || node.data?.label || node.data?.actionlabel || 'This step';
    if (!setupDone) return `Finish setup for ${name}`;
    if (!configureDone) return `Finish configuring ${name}`;
    if (!testDone) return `Test ${name} before activating`;
  }
  return null;
}

/** True if user can open the read-only panel. Based only on auth (not field mapping). */
export function isNodeReadyToOpen(node) {
  if (!node || (node.type !== 'trigger' && node.type !== 'action')) return true;
  return !stepRequiresAuth(node) || hasAuth(node);
}

/** True if every trigger and action node in the workflow is ready. Use to disable add path / filter. */
export function allStepsReady(elements) {
  if (!Array.isArray(elements)) return true;
  const nodes = elements.filter((el) => el.id && !el.source && !el.target);
  for (const node of nodes) {
    if ((node.type === 'trigger' || node.type === 'action') && !isNodeReadyToOpen(node)) return false;
  }
  return true;
}

/**
 * Returns human-readable "next steps" for a partially set-up workflow (for AI chat contextual prompt).
 * Each item: { type: 'auth' | 'fields', appLabel, nodeId, nodeType }.
 */
export function getNextStepsForWorkflow(nodes) {
  if (!Array.isArray(nodes)) return [];
  const steps = [];
  for (const node of nodes) {
    if (node.type !== 'trigger' && node.type !== 'action') continue;
    const d = node.data || {};
    const appLabel = d.label || d.app || d.type || 'App';
    if (stepRequiresAuth(node) && !hasAuth(node)) {
      steps.push({ type: 'auth', appLabel, nodeId: node.id, nodeType: node.type });
    }
    if (!isStepReady(node)) {
      steps.push({ type: 'fields', appLabel, nodeId: node.id, nodeType: node.type });
    }
  }
  return steps;
}

/** Short status / next-step message for tooltip when node is not ready (auth only). */
export function getNodeStatusMessage(node) {
  if (!node || (node.type !== 'trigger' && node.type !== 'action')) return '';
  const needsAuth = stepRequiresAuth(node) && !hasAuth(node);
  if (needsAuth) {
    return 'Connect your account in this step to continue.';
  }
  return 'Click to view step';
}
